import { useCallback, useMemo, type ReactNode } from "react";
import { getShopperGrowth } from "@/api/client";
import type { ShopperGrowthStoreRow } from "@/api/types";
import { CsvDownloadButton } from "@/components/CsvDownloadButton";
import { DataTable, type Column } from "@/components/DataTable";
import { MultiFilterSelect } from "@/components/MultiFilterSelect";
import {
  CumulativeShoppersChart,
  OptInRateChart,
  WeeklySignupsChart,
} from "@/components/ShopperGrowthCharts";
import { StatCard } from "@/components/StatCard";
import { ChartSkeleton, ErrorState, TableSkeleton } from "@/components/states";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useApiData } from "@/hooks/useApiData";
import { useProductFilterOptions } from "@/hooks/useProductFilters";
import { useUrlListFilter } from "@/hooks/useUrlFilters";
import { formatCompact, formatNumber } from "@/lib/format";
import { formatOptInRate, optInRate, withCumulative } from "@/lib/shopperGrowth";

function storeColumns(windowWeeks: number): Column<ShopperGrowthStoreRow>[] {
  const period = `past ${windowWeeks} weeks`;
  return [
    { header: "Store", cell: (r) => r.storeName, sortValue: (r) => r.storeName },
    {
      header: "Total registered",
      headerTooltip:
        "Shoppers whose home store this is, set by a purchase, a redemption, or a marketing opt-in there, all time",
      align: "right",
      cell: (r) => formatNumber(r.registered),
      sortValue: (r) => r.registered,
    },
    {
      header: "Total marketing opt-ins",
      headerTooltip: "Of the total registered, those currently opted in to marketing",
      align: "right",
      cell: (r) => formatNumber(r.optedIn),
      sortValue: (r) => r.optedIn,
    },
    {
      header: "Opt-in rate",
      headerTooltip: "Total marketing opt-ins as a share of total registered",
      align: "right",
      cell: (r) => formatOptInRate(r.optedIn, r.registered),
      sortValue: (r) => optInRate(r.optedIn, r.registered),
    },
    {
      header: `Registered, ${period}`,
      headerTooltip: `Shoppers who signed up in the ${period}, including the current week to date`,
      align: "right",
      cell: (r) => formatNumber(r.periodRegistered),
      sortValue: (r) => r.periodRegistered,
    },
    {
      header: `Marketing opt-ins, ${period}`,
      headerTooltip: `Of those recent signups, those currently opted in to marketing`,
      align: "right",
      cell: (r) => formatNumber(r.periodOptedIn),
      sortValue: (r) => r.periodOptedIn,
    },
  ];
}

function ChartCard({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
        <div className="space-y-1.5">
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
        {action}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

export function ShopperGrowthPage() {
  const filters = useProductFilterOptions();
  const [storeIds, setStoreIds, store] = useUrlListFilter("store");
  const growth = useApiData(
    useCallback(
      (token: string) => getShopperGrowth(token, { storeId: store }),
      [store],
    ),
  );

  const data = growth.status === "ready" ? growth.data : null;
  const weeks = useMemo(
    () =>
      data ? withCumulative(data.baseline, data.weekly, data.currentWeekStart) : [],
    [data],
  );
  const columns = useMemo(
    () => (data ? storeColumns(data.windowWeeks) : []),
    [data],
  );

  // Tiles read the all-time totals off the last entry, the current week to
  // date; deltas are that week's signups so far (and, for the rate, the move
  // since the last complete week).
  const last = weeks[weeks.length - 1];
  const prev = weeks[weeks.length - 2];
  const currentRate = last ? optInRate(last.cumOptedIn, last.cumRegistered) : null;
  const prevRate = prev ? optInRate(prev.cumOptedIn, prev.cumRegistered) : null;
  const ratePts =
    currentRate != null && prevRate != null ? (currentRate - prevRate) * 100 : null;

  const loading = growth.status === "loading";

  return (
    <div className="flex flex-col gap-6" aria-busy={loading}>
      <div>
        <h1 className="text-xl font-semibold">Shopper growth</h1>
        <p className="text-sm text-muted-foreground">
          New shoppers and marketing opt-ins per store by signup week. A
          shopper counts once the store is their home store, which is set by
          their first purchase, redemption, or marketing opt-in there. Totals
          are all time.
        </p>
      </div>

      <div className="flex flex-wrap gap-4">
        <MultiFilterSelect
          label="Store"
          allLabel="All stores"
          values={storeIds}
          onChange={setStoreIds}
          disabled={filters.disabled}
          options={filters.storeOptions}
        />
      </div>

      {growth.status === "error" ? (
        <ErrorState message={growth.message} onRetry={growth.reload} />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <StatCard
              label="Total registered shoppers"
              value={last && formatCompact(last.cumRegistered)}
              delta={
                last && last.registered > 0
                  ? { text: `+${formatNumber(last.registered)} this week so far`, direction: "up" }
                  : undefined
              }
            />
            <StatCard
              label="Total marketing opt-ins"
              value={last && formatCompact(last.cumOptedIn)}
              delta={
                last && last.optedIn > 0
                  ? { text: `+${formatNumber(last.optedIn)} this week so far`, direction: "up" }
                  : undefined
              }
            />
            <StatCard
              label="Opt-in rate"
              value={last && formatOptInRate(last.cumOptedIn, last.cumRegistered)}
              delta={
                ratePts != null && Math.abs(ratePts) >= 0.05
                  ? {
                      text: `${ratePts > 0 ? "+" : "−"}${Math.abs(ratePts).toFixed(1)} pts vs last week`,
                      direction: ratePts > 0 ? "up" : "down",
                    }
                  : undefined
              }
            />
          </div>

          <ChartCard
            title="Weekly signups"
            description="New shoppers by the week they registered. Opt-in uses the same signup date, so each bar is one week's new shoppers, and the orange top is the share currently opted in. The lighter last bar is the current week to date."
          >
            {loading ? <ChartSkeleton height={260} /> : <WeeklySignupsChart weeks={weeks} />}
          </ChartCard>

          <ChartCard
            title="Cumulative shoppers"
            description="All-time running totals through each week. The axis is zoomed to the period, so it starts near the opening total rather than zero. Opted in counts shoppers whose marketing flag is on today, so earlier weeks can shrink between refreshes as shoppers opt out."
          >
            {loading ? <ChartSkeleton height={280} /> : <CumulativeShoppersChart weeks={weeks} />}
          </ChartCard>

          <ChartCard
            title="Opt-in rate by signup week"
            description="Share of each week's new shoppers who are currently opted in. Same unit across stores, so it is the fair way to compare them."
          >
            {loading ? <ChartSkeleton height={180} /> : <OptInRateChart weeks={weeks} />}
          </ChartCard>

          <ChartCard
            title="By store"
            description={`All-time totals per store${data ? `, with the past ${data.windowWeeks} weeks (plus the current week to date) alongside` : ""}.`}
            action={
              data ? (
                <CsvDownloadButton
                  name="shopper-growth-by-store"
                  columns={columns}
                  rows={data.stores}
                />
              ) : null
            }
          >
            {data ? (
              <DataTable
                columns={columns}
                rows={data.stores}
                rowKey={(r) => r.storeId}
                maxHeight="32rem"
                emptyMessage="No shoppers linked to these stores"
              />
            ) : (
              <TableSkeleton rows={8} />
            )}
          </ChartCard>
        </>
      )}
    </div>
  );
}
